import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Copy, Check, ExternalLink, Send, Loader2, Mail,
  Bold, Italic, Underline, List, Link2, Sparkles, Wand2,
} from "lucide-react";
import { toast } from "sonner";
import type { DigitalProposal } from "@/types/digitalProposal";

export default function DigitalProposalReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState<DigitalProposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [toEmail, setToEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [messageHtml, setMessageHtml] = useState("");
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data, error } = await supabase.from("digital_proposals").select("*").eq("id", id).single();
      if (!error) setProposal(data as unknown as DigitalProposal);
      setLoading(false);
    })();
  }, [id]);

  const shareUrl = proposal ? `${window.location.origin}/digital-proposal/${proposal.share_id}` : "";

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Share link copied!");
    setTimeout(() => setCopied(false), 1500);
  };

  const setEditorHtml = (html: string) => {
    setMessageHtml(html);
    if (editorRef.current) editorRef.current.innerHTML = html;
  };

  const openSendDialog = () => {
    if (!proposal) return;
    setToEmail(proposal.customer_email || "");
    setSubject(`Your proposal from us — ${proposal.title}`);
    const initial =
      `<p>Hi ${proposal.customer_name || "there"},</p>` +
      `<p>Your proposal is ready. Click below to review pricing options, pick colors, and sign online when you're ready.</p>` +
      `<p><a href="${shareUrl}">Review &amp; Sign your proposal</a></p>` +
      `<p>Reply to this email if you have any questions.</p>` +
      `<p>Thanks!</p>`;
    setEditorHtml(initial);
    setAiInstruction("");
    setSendOpen(true);
  };

  const exec = (cmd: string, val?: string) => {
    document.execCommand(cmd, false, val);
    if (editorRef.current) setMessageHtml(editorRef.current.innerHTML);
  };

  const insertLink = () => {
    const url = prompt("Link URL", "https://");
    if (url) exec("createLink", url);
  };

  const runAI = async (mode: "compose" | "rewrite" | "instruct") => {
    if (!proposal) return;
    setAiBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("proposal-email-ai", {
        body: {
          mode,
          instruction: aiInstruction,
          currentSubject: subject,
          currentMessage: messageHtml,
          proposal,
          shareUrl,
        },
      });
      if (error) throw error;
      if (data?.subject) setSubject(data.subject);
      if (data?.html) setEditorHtml(data.html);
      toast.success("Bridgette updated your email");
    } catch (e: any) {
      toast.error(e?.message || "AI failed");
    } finally {
      setAiBusy(false);
    }
  };

  const sendToCustomer = async () => {
    if (!proposal) return;
    if (!toEmail.trim()) {
      toast.error("Add a customer email first");
      return;
    }
    setSending(true);
    try {
      const html = `
        <div style="font-family: Arial, sans-serif; max-width: 560px; margin: 0 auto; color: #111;">
          <div style="font-size: 15px; line-height: 1.55; color: #222;">${messageHtml}</div>
          <div style="margin-top: 24px;">
            <a href="${shareUrl}" style="background:#2563eb;color:#fff;padding:12px 20px;border-radius:8px;text-decoration:none;font-weight:600;display:inline-block;">
              Review &amp; Sign Proposal
            </a>
          </div>
        </div>
      `;

      const text = messageHtml.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

      const { error } = await supabase.functions.invoke("send-email", {
        body: { to: toEmail.trim(), subject, html, text },
      });
      if (error) throw error;

      await supabase
        .from("digital_proposals")
        .update({
          status: "sent",
          sent_at: new Date().toISOString(),
          customer_email: toEmail.trim(),
        })
        .eq("id", proposal.id);

      setProposal({ ...proposal, status: "sent", customer_email: toEmail.trim() } as DigitalProposal);
      setSendOpen(false);
      toast.success(`Proposal sent to ${toEmail.trim()}`);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const markSent = async () => {
    if (!proposal) return;
    await supabase
      .from("digital_proposals")
      .update({ status: "sent", sent_at: new Date().toISOString() })
      .eq("id", proposal.id);
    toast.success("Marked as sent");
    setProposal({ ...proposal, status: "sent" });
  };

  if (loading) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  if (!proposal) return <div className="p-12 text-center">Not found</div>;

  return (
    <div className="container mx-auto px-6 py-6 max-w-5xl">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4 gap-2">
        <ArrowLeft className="w-4 h-4" /> Back
      </Button>

      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold">{proposal.title}</h1>
            <Badge>{proposal.status}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">For {proposal.customer_name}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={copyLink} className="gap-2">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            Copy Share Link
          </Button>
          <Button variant="outline" onClick={() => window.open(shareUrl, "_blank")} className="gap-2">
            <ExternalLink className="w-4 h-4" /> Open Customer View
          </Button>
          <Button onClick={openSendDialog} className="gap-2">
            <Mail className="w-4 h-4" />
            {proposal.status === "draft" ? "Send to Customer" : "Resend Email"}
          </Button>
          {proposal.status === "draft" && (
            <Button variant="ghost" onClick={markSent} className="gap-2">
              <Send className="w-4 h-4" /> Mark as Sent
            </Button>
          )}
        </div>
      </div>

      <Card className="p-6 mb-6">
        <h2 className="font-semibold mb-3">Tiers</h2>
        <div className="grid md:grid-cols-3 gap-3">
          {proposal.tiers.map((t) => (
            <div key={t.key} className="border rounded-lg p-4">
              <div className="text-xs uppercase text-muted-foreground">{t.key}</div>
              <div className="font-semibold">{t.name}</div>
              {t.manufacturer && (
                <div className="text-xs text-muted-foreground">{t.manufacturer} · {t.product_line}</div>
              )}
              <div className="text-2xl font-bold mt-2">${t.price.toLocaleString()}</div>
              <div className="flex gap-1 mt-2">
                {(t.colors || []).slice(0, 6).map((c) => (
                  <div key={c.hex} className="w-4 h-4 rounded-full border" style={{ backgroundColor: c.hex }} title={c.name} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {proposal.status === "signed" && (
        <Card className="p-6 bg-success/5 border-success/30">
          <h2 className="font-semibold mb-2">✓ Signed</h2>
          <p className="text-sm">
            By <strong>{proposal.signed_by_name}</strong>
            {proposal.signed_at ? ` on ${new Date(proposal.signed_at).toLocaleString()}` : ""}
          </p>
          {proposal.selected_tier && (
            <p className="text-sm mt-1">
              Selected: <strong>{proposal.tiers.find((t) => t.key === proposal.selected_tier)?.name}</strong>
              {proposal.selected_color ? ` in ${proposal.selected_color.name}` : ""}
            </p>
          )}
          {proposal.signature_data && (
            <img src={proposal.signature_data} alt="signature" className="h-16 mt-3 bg-white border rounded" />
          )}
        </Card>
      )}

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Send Proposal to Customer</DialogTitle>
            <DialogDescription>
              We'll email a branded link the customer can review, choose options, and sign online.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label>To</Label>
              <Input
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                placeholder="customer@email.com"
              />
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Message</Label>
                <div className="flex gap-1">
                  <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("bold")} title="Bold">
                    <Bold className="w-3.5 h-3.5" />
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("italic")} title="Italic">
                    <Italic className="w-3.5 h-3.5" />
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("underline")} title="Underline">
                    <Underline className="w-3.5 h-3.5" />
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertUnorderedList")} title="List">
                    <List className="w-3.5 h-3.5" />
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onMouseDown={(e) => e.preventDefault()} onClick={insertLink} title="Link">
                    <Link2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={(e) => setMessageHtml((e.target as HTMLDivElement).innerHTML)}
                className="min-h-[180px] max-h-[320px] overflow-y-auto rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring [&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_p]:my-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                A branded "Review &amp; Sign" button is added below your message automatically.
              </p>
            </div>

            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Sparkles className="w-4 h-4 text-primary" /> Bridgette AI Assistant
              </div>
              <Input
                value={aiInstruction}
                onChange={(e) => setAiInstruction(e.target.value)}
                placeholder='Tell Bridgette what to write — e.g. "shorter and more urgent", "mention storm damage", "add a warm intro"'
                className="bg-background"
              />
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="secondary" disabled={aiBusy} onClick={() => runAI("compose")} className="gap-1">
                  {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                  Write for me
                </Button>
                <Button type="button" size="sm" variant="outline" disabled={aiBusy} onClick={() => runAI("rewrite")} className="gap-1">
                  <Sparkles className="w-3.5 h-3.5" /> Improve current
                </Button>
                <Button type="button" size="sm" variant="outline" disabled={aiBusy || !aiInstruction.trim()} onClick={() => runAI("instruct")} className="gap-1">
                  <Sparkles className="w-3.5 h-3.5" /> Apply instruction
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)} disabled={sending}>
              Cancel
            </Button>
            <Button onClick={sendToCustomer} disabled={sending} className="gap-2">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
